import { postAuthJson, SESSION_LOGOUT_PATH } from "./auth-http";
import { stopSession } from "./session";
import { clearLogoutPending, isLogoutPending, markLogoutPending } from "./session-store";

export type LogoutOutcome = "completed" | "pending";

export const LOGOUT_PENDING_CODE = "logout_pending";
export const LOGOUT_PENDING_MESSAGE =
  "We couldn't finish signing out your previous session. Check your connection and try again.";

function retryWhenOnline() {
  window.addEventListener(
    "online",
    () => {
      if (isLogoutPending()) {
        void sendPendingLogout();
      }
    },
    { once: true },
  );
}

export async function sendPendingLogout(): Promise<LogoutOutcome> {
  let response: Response;
  try {
    response = await postAuthJson(SESSION_LOGOUT_PATH);
  } catch {
    retryWhenOnline();
    return "pending";
  }

  if (response.status !== 204) {
    return "pending";
  }
  clearLogoutPending();
  return "completed";
}

// Sign-in must wait for a confirmed logout: a late retry would revoke the new refresh
// cookie, and startSession() would drop the marker while the old session stays active.
export async function finishPendingLogout(): Promise<boolean> {
  if (!isLogoutPending()) {
    return true;
  }
  return (await sendPendingLogout()) === "completed";
}

// The marker must be written before the request: if the browser goes offline, the
// next page load sees it and logs out instead of restoring the session from the cookie.
export function logout(): Promise<LogoutOutcome> {
  markLogoutPending();
  stopSession();
  return sendPendingLogout();
}
